import { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Polygon, Popup, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import CustomForm from '../../../core/components/core/Util/Form/CustomForm/CustomForm';
import CustomListContainer from '../../../core/components/core/Util/List/CustomList/CustomListContainer';
import Fetch from '../../../core/util/core/Fetch';
import Toast from '../../../core/components/core/Util/Toast/Toast';
import config from '../../config/config';
import { chooseProfileFilter } from '../../helpers/chooseProfileFilter';
import { toLeafletPositions } from '../Dashboards/Censo/Common/hooks/useCensusTracts';
import { boundsOf } from '../Dashboards/Censo/Common/CensusMap';
import './SectorsLink.css';

const LINKED_COLOR = '#4292c6';
const UNLINKED_COLOR = '#adb5bd';
const SELECTED_COLOR = '#fd7e14';

// Não vinculado: preenchimento neutro e mais transparente, com borda azul para o limite do setor
// aparecer sobre o mapa.
const STYLE_BY_COLOR = {
    [LINKED_COLOR]: { color: '#08519c', fillOpacity: 0.6 },
    [UNLINKED_COLOR]: { color: '#1c7ed6', fillOpacity: 0.4 },
    [SELECTED_COLOR]: { color: '#c2410c', fillOpacity: 0.6 }
};

const endpoints = {
    states: `${config.endpoints.backend}governanceroom/states/getall`,
    cities: `${config.endpoints.backend}governanceroom/cities/getall`,
    neighborhoods: `${config.endpoints.backend}governanceroom/neighborhoods/getall`,
    map: `${config.endpoints.backend}governanceroom/sectors/map`,
    link: `${config.endpoints.backend}governanceroom/sectors/link`
};

/**
 * Popup do setor clicado: escolhe o bairro e vincula (ou desvincula) o setor censitário.
 */
function LinkPopupContent({ cityId, tract, neighborhoods, onLinked }) {
    const [neighborhoodId, setNeighborhoodId] = useState(tract.neighborhoodId || '');
    const [isSaving, setIsSaving] = useState(false);

    const save = async newNeighborhoodId => {
        setIsSaving(true);
        try {
            const result = await Fetch.put(endpoints.link, {
                city_id: cityId,
                sector_id: tract.id,
                governance_room_neighborhood_id: newNeighborhoodId
            });

            // Fetch.put não rejeita em erro HTTP: devolve o corpo { message }.
            if (result?.status !== 1) {
                throw new Error(result?.message || 'Erro ao vincular o setor censitário.');
            }

            Toast.success(newNeighborhoodId ? 'Setor censitário vinculado.' : 'Setor censitário desvinculado.');
            onLinked(tract.id, newNeighborhoodId);
        } catch (error) {
            Toast.error(error.message);
            setIsSaving(false);
        }
    };

    return (
        <div className="sl-popup">
            <strong>Setor Censitário {tract.id}</strong>
            <dl className="sl-hierarchy">
                <dt>Bairro</dt>
                <dd>{tract.neighborhood || 'Não Vinculado'}</dd>
                {tract.neighborhoodId && (
                    <>
                        <dt>Sub-Grupo</dt>
                        <dd>{tract.subGroup || 'Não Vinculado'}</dd>
                        <dt>Grupo</dt>
                        <dd>{tract.group || 'Não Vinculado'}</dd>
                    </>
                )}
            </dl>

            <label htmlFor="sl-neighborhood">Bairro</label>
            <select
                id="sl-neighborhood"
                className="form-control form-control-sm"
                value={neighborhoodId}
                onChange={event => setNeighborhoodId(event.target.value)}
                disabled={isSaving}
            >
                <option value="">Selecione</option>
                {neighborhoods.map(({ value, label }) => (
                    <option key={value} value={value}>
                        {label}
                    </option>
                ))}
            </select>
            {!neighborhoods.length && <span className="sl-muted">Nenhum bairro cadastrado para esta cidade.</span>}

            <div className="sl-popup-actions">
                <button
                    type="button"
                    className="btn btn-sm btn-info"
                    disabled={isSaving || !neighborhoodId || neighborhoodId === tract.neighborhoodId}
                    onClick={() => save(neighborhoodId)}
                >
                    Vincular
                </button>
                {tract.neighborhoodId && (
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        disabled={isSaving}
                        onClick={() => save(null)}
                    >
                        Desvincular
                    </button>
                )}
            </div>
        </div>
    );
}

/**
 * Vincular Setores Censitários: mapa dos setores do município; o clique no setor abre o popup para vinculá-lo
 * a um bairro. Só aparecem os setores com desenho (malha GeoJSON importada).
 */
function SectorsLink() {
    const profileFilter = chooseProfileFilter();
    const [filterValues, setFilterValues] = useState({ geografic_state_id: '', city_id: '' });
    const [data, setData] = useState({ status: 'idle', tracts: [], neighborhoods: [] });
    // Um popup só no mapa, no ponto clicado: { tractId, latlng }.
    const [popup, setPopup] = useState(null);

    const cityId = filterValues.city_id;

    const handleChange = element => {
        const { name, value } = element.target;
        // trocar o estado invalida o município escolhido
        setFilterValues(current => ({
            ...current,
            ...(name === 'geografic_state_id' ? { city_id: '' } : {}),
            [name]: value
        }));
    };

    useEffect(() => {
        setPopup(null);
        if (!cityId) {
            setData({ status: 'idle', tracts: [], neighborhoods: [] });
            return;
        }

        let isCurrent = true;
        setData({ status: 'loading', tracts: [], neighborhoods: [] });

        Promise.all([
            Fetch.post(endpoints.map, { city_id: cityId }),
            Fetch.post(endpoints.neighborhoods, { filter: { city_id: cityId } })
        ])
            .then(([tracts, neighborhoods]) => {
                // Fetch.post não rejeita em erro HTTP: devolve o corpo { message }.
                if (!Array.isArray(tracts)) throw new Error(tracts?.message || 'Erro ao buscar os setores censitários.');
                if (!Array.isArray(neighborhoods)) throw new Error('Erro ao buscar os bairros.');
                if (!isCurrent) return;

                setData({
                    status: 'done',
                    tracts: tracts
                        .map(({ geometry, ...tract }) => ({ ...tract, polygon: toLeafletPositions(geometry) }))
                        .filter(tract => tract.polygon),
                    neighborhoods: neighborhoods.map(row => ({
                        value: row.id,
                        label: `${row.description} (${row.sub_group_name})`,
                        name: row.description,
                        subGroup: row.sub_group_name,
                        group: row.group_name
                    }))
                });
            })
            .catch(error => isCurrent && setData({ status: 'error', message: error.message, tracts: [], neighborhoods: [] }));

        return () => {
            isCurrent = false;
        };
    }, [cityId]);

    // Atualiza o setor no mapa sem recarregar a cidade.
    const onLinked = (tractId, neighborhoodId) => {
        const neighborhood = data.neighborhoods.find(item => item.value === neighborhoodId);
        setData(current => ({
            ...current,
            tracts: current.tracts.map(tract =>
                tract.id === tractId
                    ? {
                        ...tract,
                        neighborhoodId,
                        neighborhood: neighborhood ? neighborhood.name : null,
                        subGroup: neighborhood ? neighborhood.subGroup : null,
                        group: neighborhood ? neighborhood.group : null
                    }
                    : tract
            )
        }));
        setPopup(null);
    };

    const form = useMemo(() => ({
        validateOnChange: true,
        showGoBackButton: false,
        showSubmitButton: false,
        update_key: `${filterValues.geografic_state_id}-${filterValues.city_id}`,
        handleSubmit: () => {},
        fields: [
            {
                name: 'geografic_state_id',
                type: 'select',
                label: 'Estado',
                handleChange,
                options: [],
                optionsEndpoint: {
                    endpoint: endpoints.states,
                    label: 'geografic_state_name',
                    value: 'geografic_state_id'
                },
                validations: { required: false },
                columnSize: 4
            },
            {
                name: 'city_id',
                type: 'select',
                label: 'Município',
                handleChange,
                options: [],
                optionsEndpoint: {
                    endpoint: endpoints.cities,
                    label: 'city_name',
                    value: 'city_id',
                    params: {
                        filter: {
                            where: {
                                geografic_state_id: filterValues.geografic_state_id,
                                profileFilter
                            }
                        }
                    }
                },
                validations: { required: false },
                columnSize: 4
            }
        ],
        values: filterValues
    }), [filterValues]);

    const { tracts } = data;
    const bounds = useMemo(() => (tracts.length ? boundsOf(tracts) : null), [tracts]);
    const linkedCount = tracts.filter(tract => tract.neighborhoodId).length;
    const popupTract = popup && tracts.find(tract => tract.id === popup.tractId);

    let body;
    if (!cityId) {
        body = <div className="sl-notice">Selecione o estado e o município para ver os setores censitários.</div>;
    } else if (data.status === 'loading') {
        body = <div className="sl-notice"><i className="fas fa-spinner fa-spin" /> Carregando...</div>;
    } else if (data.status === 'error') {
        body = <div className="sl-notice sl-notice-error">{data.message}</div>;
    } else if (!tracts.length) {
        body = (
            <div className="sl-notice">
                Nenhum setor censitário com desenho para este município. Importe a malha de setores (GeoJSON) no
                módulo de arquivos.
            </div>
        );
    } else {
        body = (
            <>
                <div className="sl-summary">
                    <span>
                        <strong>{linkedCount}</strong> de <strong>{tracts.length}</strong> setores censitários
                        vinculados a um bairro. Clique em um setor para vinculá-lo.
                    </span>
                    <span className="sl-legend">
                        <i style={{ background: LINKED_COLOR, borderColor: STYLE_BY_COLOR[LINKED_COLOR].color }} /> Vinculado
                        <i style={{ background: UNLINKED_COLOR, borderColor: STYLE_BY_COLOR[UNLINKED_COLOR].color }} /> Não Vinculado
                    </span>
                </div>

                {/* key: remonta o mapa ao trocar de cidade, para reenquadrar nos novos setores. */}
                <MapContainer
                    key={cityId}
                    bounds={bounds}
                    boundsOptions={{ padding: [8, 8] }}
                    zoomSnap={0.25}
                    zoomDelta={0.5}
                    preferCanvas
                    className="sl-map"
                >
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {tracts.map(tract => {
                        const fillColor =
                            popupTract && popupTract.id === tract.id
                                ? SELECTED_COLOR
                                : tract.neighborhoodId
                                    ? LINKED_COLOR
                                    : UNLINKED_COLOR;
                        return (
                            <Polygon
                                key={tract.id}
                                positions={tract.polygon}
                                pathOptions={{
                                    ...STYLE_BY_COLOR[fillColor],
                                    weight: 1.5,
                                    opacity: 1,
                                    fillColor
                                }}
                                eventHandlers={{ click: event => setPopup({ tractId: tract.id, latlng: event.latlng }) }}
                            >
                                <Tooltip sticky>
                                    Setor Censitário {tract.id}: {tract.neighborhood || 'Não Vinculado'}
                                </Tooltip>
                            </Polygon>
                        );
                    })}
                    {popupTract && (
                        // key: remonta a cada clique (o Leaflet fecha o popup aberto no clique do mapa),
                        // para abrir no novo ponto com o conteúdo do setor clicado.
                        <Popup
                            key={`${popupTract.id}-${popup.latlng.lat}-${popup.latlng.lng}`}
                            position={popup.latlng}
                            eventHandlers={{ remove: () => setPopup(null) }}
                        >
                            <LinkPopupContent
                                cityId={cityId}
                                tract={popupTract}
                                neighborhoods={data.neighborhoods}
                                onLinked={onLinked}
                            />
                        </Popup>
                    )}
                </MapContainer>
            </>
        );
    }

    const content = (
        <div className="card-body sl-body">
            <CustomForm form={form} key={form.update_key} />
            {body}
        </div>
    );

    return (
        <CustomListContainer
            canAdd={false}
            labelTitle="Vincular Setores Censitários"
            labelTitleButtonAdd=""
            content={content}
        />
    );
}

export default SectorsLink;
